import styled from 'styled-components'
import { omniaTheme } from '../../theme/omniaTheme'

/**
 * Omnia signature page-title pattern:
 *   <SectionTitle>La propri&eacute;t&eacute;</SectionTitle>
 *   <TitleUnderline />        <- thin hairline spanning the full content width
 *
 * Exception: cover and Location Excellence use the LargeTitle variant.
 * See ai/mockups/omnia/brand-guide.md section 4.
 */
export const SectionTitleRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 12px;

  @media print {
    display: flex !important;
    align-items: baseline !important;
    justify-content: space-between !important;
    margin-bottom: 12px !important;
  }
`

export const SectionTitle = styled.h2`
  font-family: ${omniaTheme.fonts.heading};
  font-weight: ${omniaTheme.fontWeights.light};
  font-size: 32px;
  color: ${omniaTheme.colors.black};
  letter-spacing: ${omniaTheme.letterSpacing.tight};
  line-height: 1.05;
  margin: 0;

  @media print {
    font-family: ${omniaTheme.fonts.heading} !important;
    font-weight: ${omniaTheme.fontWeights.light} !important;
    font-size: 32px !important;
    color: ${omniaTheme.colors.black} !important;
    letter-spacing: ${omniaTheme.letterSpacing.tight} !important;
    line-height: 1.05 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const SectionTitlePageNumber = styled.span`
  font-family: ${omniaTheme.fonts.label};
  font-weight: ${omniaTheme.fontWeights.light};
  font-size: 11px;
  color: ${omniaTheme.colors.textLabel};
  letter-spacing: ${omniaTheme.letterSpacing.wide};

  @media print {
    font-family: ${omniaTheme.fonts.label} !important;
    font-size: 11px !important;
    color: ${omniaTheme.colors.textLabel} !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

/**
 * Full-width hairline beneath every page title — the brand's defining
 * visual rule. Uses #2F2252 with no transparency so the line reads clearly
 * but never as pure black.
 */
export const TitleUnderline = styled.div`
  width: 100%;
  height: 0.5px;
  background: ${omniaTheme.colors.strokeLine};
  margin: 0 0 28px 0;

  @media print {
    width: 100% !important;
    height: 0.5px !important;
    background: ${omniaTheme.colors.strokeLine} !important;
    margin: 0 0 28px 0 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

/**
 * LargeTitle variant — used on the cover and Location Excellence.
 * Two-line display headline (32px+) instead of the 22px section title.
 * Followed by a full-width hairline divider rather than the 64px underline.
 */
export const LargeTitle = styled.h1`
  font-family: ${omniaTheme.fonts.heading};
  font-weight: ${omniaTheme.fontWeights.light};
  font-size: ${omniaTheme.fontSizes.huge};
  color: ${omniaTheme.colors.black};
  letter-spacing: ${omniaTheme.letterSpacing.tight};
  line-height: 1.05;
  margin: 0;

  @media print {
    font-family: ${omniaTheme.fonts.heading} !important;
    font-weight: ${omniaTheme.fontWeights.light} !important;
    font-size: ${omniaTheme.fontSizes.huge} !important;
    color: ${omniaTheme.colors.black} !important;
    letter-spacing: ${omniaTheme.letterSpacing.tight} !important;
    line-height: 1.05 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const FullWidthDivider = styled.hr`
  width: 100%;
  height: 0;
  border: 0;
  border-top: 0.5px solid ${omniaTheme.colors.strokeLine};
  margin: 16px 0 24px 0;

  @media print {
    border-top: 0.5px solid ${omniaTheme.colors.strokeLine} !important;
    margin: 16px 0 24px 0 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

/**
 * Roboto Condensed UPPERCASE label — used everywhere a small label
 * sits above or below content. e.g. metric-card unit labels, AVM strip names.
 */
export const Label = styled.span`
  font-family: ${omniaTheme.fonts.label};
  font-weight: ${omniaTheme.fontWeights.light};
  font-size: ${omniaTheme.fontSizes.label};
  color: ${omniaTheme.colors.textLabel};
  letter-spacing: ${omniaTheme.letterSpacing.wide};
  text-transform: uppercase;

  @media print {
    font-family: ${omniaTheme.fonts.label} !important;
    font-size: ${omniaTheme.fontSizes.label} !important;
    color: ${omniaTheme.colors.textLabel} !important;
    letter-spacing: ${omniaTheme.letterSpacing.wide} !important;
    text-transform: uppercase !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`
